import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui-kit/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/ui-kit/accordian';
import { Button } from '@/ui-kit/button';
import { Badge } from '@/ui-kit/badge';
import { Label } from '@/ui-kit/label';
import Headline from '@/ui-kit/Typeography/Headline';
import type { ProductAttribute } from '@/shared/types';
import { useAttributeCountStore } from '@/stores/useAttributeCountStore';
import { useFullFilterStore } from '@/stores/useFullFilterStore';
import { useTutorialStore } from '@/components/tutorials/useTutorialStore';
import { OnboardingAttributePicker } from './OnboardingAttributePicker';
import { OnboardingInfoIcon, OnboardingInfoProvider } from './OnboardingInfoIcon';
import {
  ONBOARDING_ATTRIBUTES,
  ONBOARDING_QUESTION_STEPS,
  ONBOARDING_USAGE_HELP,
  type OnboardingStepId,
} from './onboardingSteps';
import {
  buildFilterExpressionFromSelections,
  hasAnyOnboardingSelection,
  onboardingSelectionsToOrAst,
  type OnboardingSelections,
} from './buildOnboardingFilter';
import { onboardingSectionCardClass } from './onboardingSectionCard';

const emptySelections = (): OnboardingSelections =>
  Object.fromEntries(ONBOARDING_QUESTION_STEPS.map((s) => [s.id, []])) as OnboardingSelections;

export const GuidedFilterOnboardingModal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { guidedSetupOpen, closeGuidedSetup, startGuidedFilterPostApply } = useTutorialStore();
  const { fullCounts, loadedAttributes, loadAttribute } = useAttributeCountStore();
  const { setFilterAst } = useFullFilterStore();

  const [selections, setSelections] = useState<OnboardingSelections>(emptySelections);

  useEffect(() => {
    if (!guidedSetupOpen) return;
    setSelections(emptySelections());
    for (const attr of ONBOARDING_ATTRIBUTES) {
      void loadAttribute(attr);
    }
  }, [guidedSetupOpen, loadAttribute]);

  const updateSelection = useCallback((stepId: OnboardingStepId, values: string[]) => {
    setSelections((prev) => ({ ...prev, [stepId]: values }));
  }, []);

  const optionsForAttribute = useCallback(
    (attribute: ProductAttribute) => {
      const counts = fullCounts[attribute] || {};
      return Object.entries(counts).map(([value, count]) => ({ value, count }));
    },
    [fullCounts]
  );

  const filterPreview = useMemo(
    () => buildFilterExpressionFromSelections(selections),
    [selections]
  );

  const canApplyFilter = hasAnyOnboardingSelection(selections);

  const handleApplyToDashboard = () => {
    if (!canApplyFilter) {
      toast.error('Select at least one option to build a filter');
      return;
    }
    const ast = onboardingSelectionsToOrAst(selections);
    if (ast) {
      setFilterAst(ast);
    }
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
    startGuidedFilterPostApply();
  };

  if (!guidedSetupOpen) return null;

  return (
    <Dialog open={guidedSetupOpen} onOpenChange={(open) => !open && closeGuidedSetup()}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <OnboardingInfoProvider>
          <DialogHeader className="relative shrink-0 px-6 pt-6 pb-2 pr-20 space-y-2">
            <OnboardingInfoIcon
              content={ONBOARDING_USAGE_HELP}
              side="bottom"
              align="end"
              className="absolute right-12 top-5"
            />
            <DialogTitle>First time Onboarding Guide</DialogTitle>
            <DialogDescription>
              This walkthrough helps you build an initial dashboard filter from threat intelligence
              attributes so you can start exploring the platform with data that matters to you.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4 space-y-3">
            <Accordion type="multiple" defaultValue={[]} className="space-y-3">
              {ONBOARDING_QUESTION_STEPS.map((step) => (
                <AccordionItem
                  key={step.id}
                  value={step.id}
                  className={`border-b-0 ${onboardingSectionCardClass}`}
                >
                  <AccordionTrigger
                    className="px-[1.35rem] py-3 hover:no-underline [&[data-state=open]>svg]:rotate-180"
                    aria-labelledby={`onboarding-${step.id}-title`}
                  >
                    <div className="flex flex-1 items-center pr-2 text-left">
                      <Headline as="h3" id={`onboarding-${step.id}-title`}>
                        {step.title}
                      </Headline>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-[1.35rem] pb-[1.35rem]">
                    <OnboardingAttributePicker
                      layout="section"
                      options={optionsForAttribute(step.attribute)}
                      selected={selections[step.id] ?? []}
                      onChange={(vals) => updateSelection(step.id, vals)}
                      isLoading={!loadedAttributes.has(step.attribute)}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <section className={`space-y-3 p-[1.35rem] ${onboardingSectionCardClass}`}>
              <Headline as="h3">Your filter</Headline>
              <p className="text-sm text-muted-foreground">
                The filter below is created from the values you selected above. Matching products from
                any selected category will be included.
              </p>
              {!canApplyFilter && (
                <p className="text-sm text-destructive">
                  Select at least one value in any category above to build a filter.
                </p>
              )}
              {canApplyFilter && (
                <div className="flex flex-wrap gap-1">
                  {ONBOARDING_QUESTION_STEPS.map((step) =>
                    (selections[step.id] ?? []).map((v) => (
                      <Badge
                        key={`${step.id}-${v}`}
                        variant="secondary"
                        className="text-[10px] font-normal"
                      >
                        {v.length > 28 ? `${v.slice(0, 25)}…` : v}
                      </Badge>
                    ))
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">FilterQL preview</Label>
                <pre className="mt-1 text-xs p-3 rounded-md border border-border bg-muted/40 text-foreground whitespace-pre-wrap break-all font-mono max-h-32 overflow-y-auto">
                  {filterPreview || '(select values above)'}
                </pre>
              </div>
            </section>
          </div>
        </OnboardingInfoProvider>

        <div className="flex flex-wrap justify-end gap-2 px-6 py-4 border-t border-border shrink-0 bg-popover">
          <Button type="button" variant="ghost" size="sm" onClick={closeGuidedSetup}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canApplyFilter}
            onClick={handleApplyToDashboard}
          >
            Apply to dashboard
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
